// src/services/orderService.js
import { 
    collection, 
    doc, 
    getDoc, 
    getDocs, 
    updateDoc, 
    query, 
    where, 
    orderBy, 
    limit,
    startAfter,
    Timestamp,
    serverTimestamp 
  } from 'firebase/firestore';
  import { db } from './firebase';
  
  /**
   * Get orders for a vendor with pagination
   * @param {string} vendorId - Vendor ID
   * @param {Object} options - Query options
   * @returns {Object} - { orders, lastDoc, total }
   */
  export const getOrders = async (vendorId, options = {}) => {
    try {
      const {
        pageSize = 10,
        lastVisible = null,
        status = null,
        customerId = null,
        dateRange = null, // { startDate, endDate }
        search = ''
      } = options;
      
      // Create base query
      const ordersRef = collection(db, 'orders');
      let q = query(
        ordersRef,
        where('vendorId', '==', vendorId),
        orderBy('createdAt', 'desc')
      );
      
      // Apply filters
      if (status) {
        q = query(q, where('status', '==', status));
      }
      
      if (customerId) {
        q = query(q, where('customerId', '==', customerId));
      }
      
      if (dateRange && dateRange.startDate && dateRange.endDate) {
        const startTimestamp = Timestamp.fromDate(new Date(dateRange.startDate));
        const endTimestamp = Timestamp.fromDate(new Date(dateRange.endDate));
        
        q = query(
          q,
          where('createdAt', '>=', startTimestamp),
          where('createdAt', '<=', endTimestamp)
        );
      }
      
      // Apply pagination
      if (lastVisible) {
        q = query(q, startAfter(lastVisible), limit(pageSize));
      } else {
        q = query(q, limit(pageSize));
      }
      
      // Execute query
      const querySnapshot = await getDocs(q);
      
      // Process orders
      const orders = [];
      querySnapshot.forEach((doc) => {
        const orderData = doc.data();
        
        // Apply search filter if provided
        if (search) {
          const orderId = doc.id.toLowerCase();
          const customerName = orderData.customerName?.toLowerCase() || '';
          const customerEmail = orderData.customerEmail?.toLowerCase() || '';
          const customerPhone = orderData.customerPhone?.toLowerCase() || '';
          const searchLower = search.toLowerCase();
          
          // Skip if doesn't match search
          if (!orderId.includes(searchLower) && 
              !customerName.includes(searchLower) && 
              !customerEmail.includes(searchLower) &&
              !customerPhone.includes(searchLower)) {
            return;
          }
        }
        
        orders.push({
          id: doc.id,
          ...orderData
        });
      });
      
      // Get last visible document for pagination
      const lastDoc = querySnapshot.docs[querySnapshot.docs.length - 1];
      
      // Get total count (for a real app, you might want to use a more efficient way)
      const totalQuery = query(
        ordersRef,
        where('vendorId', '==', vendorId)
      );
      const totalSnapshot = await getDocs(totalQuery);
      const total = totalSnapshot.size;
      
      return { orders, lastDoc, total };
    } catch (error) {
      console.error('Error getting orders:', error);
      throw error;
    }
  };
  
  /**
   * Get a single order by ID
   * @param {string} orderId - Order ID
   * @returns {Object} - Order data
   */
  export const getOrderById = async (orderId) => {
    try {
      const orderRef = doc(db, 'orders', orderId);
      const orderSnap = await getDoc(orderRef);
      
      if (orderSnap.exists()) {
        return {
          id: orderSnap.id,
          ...orderSnap.data()
        };
      } else {
        throw new Error('Order not found');
      }
    } catch (error) {
      console.error('Error getting order:', error);
      throw error;
    }
  };
  
  /**
   * Update order status
   * @param {string} orderId - Order ID
   * @param {string} status - New status
   * @param {Object} additionalData - Additional data to update
   * @returns {Object} - Updated order
   */
  export const updateOrderStatus = async (orderId, status, additionalData = {}) => {
    try {
      const orderRef = doc(db, 'orders', orderId);
      
      // Get current order data
      const orderSnap = await getDoc(orderRef);
      if (!orderSnap.exists()) {
        throw new Error('Order not found');
      }
      
      const orderData = orderSnap.data();
      
      // Create status history entry
      const statusHistory = orderData.statusHistory || [];
      statusHistory.push({
        status,
        timestamp: new Date(),
        note: additionalData.note || ''
      });
      
      // Update order
      await updateDoc(orderRef, {
        status,
        statusHistory,
        updatedAt: new Date(),
        ...additionalData
      });
      
      // Get updated order
      const updatedOrderSnap = await getDoc(orderRef);
      
      return {
        id: orderId,
        ...updatedOrderSnap.data()
      };
    } catch (error) {
      console.error('Error updating order status:', error);
      throw error;
    }
  };
  
  /**
   * Generate invoice for an order
   * @param {string} orderId - Order ID
   * @returns {Object} - Invoice data
   */
  export const generateInvoice = async (orderId) => {
    try {
      const orderRef = doc(db, 'orders', orderId);
      
      // Get order data
      const orderSnap = await getDoc(orderRef);
      if (!orderSnap.exists()) {
        throw new Error('Order not found');
      }
      
      const orderData = orderSnap.data();
      
      // Check if invoice already exists
      if (orderData.invoice) {
        return orderData.invoice;
      }
      
      // Generate invoice number
      const invoiceNumber = `INV-${Date.now().toString().slice(-6)}-${orderId.slice(-4)}`;
      
      // Create invoice data
      const invoice = {
        invoiceNumber,
        orderId,
        customerName: orderData.customerName,
        customerEmail: orderData.customerEmail,
        customerAddress: orderData.shippingAddress,
        vendorId: orderData.vendorId,
        items: orderData.items,
        subtotal: orderData.subtotal,
        tax: orderData.tax,
        shipping: orderData.shipping,
        total: orderData.total,
        paymentMethod: orderData.paymentMethod,
        createdAt: new Date(),
        dueDate: Timestamp.fromDate(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)) // 7 days from now
      };
      
      // Update order with invoice
      await updateDoc(orderRef, {
        invoice,
        updatedAt: new Date()
      });
      
      return invoice;
    } catch (error) {
      console.error('Error generating invoice:', error);
      throw error;
    }
  };
  
  /**
   * Get order statistics
   * @param {string} vendorId - Vendor ID
   * @param {Object} dateRange - Date range { startDate, endDate }
   * @returns {Object} - Order statistics
   */
  export const getOrderStatistics = async (vendorId, dateRange = null) => {
    try {
      // Create base query
      const ordersRef = collection(db, 'orders');
      let q = query(
        ordersRef,
        where('vendorId', '==', vendorId)
      );
      
      // Apply date range filter
      if (dateRange && dateRange.startDate && dateRange.endDate) {
        const startTimestamp = Timestamp.fromDate(new Date(dateRange.startDate));
        const endTimestamp = Timestamp.fromDate(new Date(dateRange.endDate));
        
        q = query(
          q,
          where('createdAt', '>=', startTimestamp),
          where('createdAt', '<=', endTimestamp)
        );
      }
      
      // Execute query
      const querySnapshot = await getDocs(q);
      
      // Calculate statistics
      let totalOrders = 0;
      let totalRevenue = 0;
      const statusCounts = {
        Pending: 0,
        Confirmed: 0,
        Processing: 0,
        Shipped: 0,
        Delivered: 0,
        Cancelled: 0
      };
      const customerSet = new Set();
      const dailyRevenue = {};
      
      querySnapshot.forEach((doc) => {
        const orderData = doc.data();
        totalOrders++;
        totalRevenue += orderData.total || 0;
        
        // Update status counts
        const status = orderData.status || 'Pending';
        if (statusCounts[status] !== undefined) {
          statusCounts[status]++;
        }
        
        // Track unique customers
        if (orderData.customerId) {
          customerSet.add(orderData.customerId);
        }
        
        // Track daily revenue
        if (orderData.createdAt && orderData.total) {
          const date = orderData.createdAt.toDate().toISOString().split('T')[0]; // YYYY-MM-DD
          if (!dailyRevenue[date]) {
            dailyRevenue[date] = 0;
          }
          dailyRevenue[date] += orderData.total;
        }
      });
      
      // Convert daily revenue to array
      const dailyRevenueArray = Object.entries(dailyRevenue).map(([date, amount]) => ({
        date,
        amount
      })).sort((a, b) => a.date.localeCompare(b.date));
      
      return {
        totalOrders,
        totalRevenue,
        statusCounts,
        uniqueCustomers: customerSet.size,
        dailyRevenue: dailyRevenueArray,
        averageOrderValue: totalOrders > 0 ? totalRevenue / totalOrders : 0
      };
    } catch (error) {
      console.error('Error getting order statistics:', error);
      throw error;
    }
  };
  
  // Export functions
  export default {
    getOrders,
    getOrderById,
    updateOrderStatus,
    generateInvoice,
    getOrderStatistics
  };